import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useNavigate } from 'react-router-dom';
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import SubNav from '../components/subNav';
import SEO from '../components/SEO';
import { baseUrl, R2_PUBLIC_URL } from '../config';
import { PiHammerLight } from "react-icons/pi";
import axios from 'axios';

import {
  Calendar,
  Film,
  Users,
  Award,
  Briefcase,
  ChevronRight,
  ChevronLeft,
  Bell,
  ExternalLink,
  Sparkles,
  Building2,
  LogOut,
  AlertTriangle,
  MessageSquare,
  Star,
  FileText,
  Settings,
  Megaphone,
  UserPlus,
  CheckCircle,
  X,
  Send,
  Loader2
} from 'lucide-react';
import { BsTools } from "react-icons/bs";

// Lazy load components for better performance
const LazyApplyProjects = lazy(() => import('../pages/applyprojects'));
const LazyEnthusiastSchedule = lazy(() => import('../pages/enthusiast-schedule'));
const LazyMyProjects = lazy(() => import('../pages/myprojects'));

// ==================== API HELPER FUNCTIONS ====================

// Get JWT token from localStorage
const getAuthToken = () => {
  return localStorage.getItem('access_token');
};

// Create authenticated axios instance
const createAuthAxios = () => {
  const token = getAuthToken();
  return axios.create({
    baseURL: baseUrl,
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });
};

// ==================== ENTHUSIAST API CALLS (ALL INLINE) ====================

// Dashboard Overview - Fetches project_activity, notifications, auditions, stats
const getDashboardOverview = async () => {
  const api = createAuthAxios();
  const response = await api.get('/enthusiast/dashboard');
  return response.data;
};

// Skills Management
const getSkills = async () => {
  const api = createAuthAxios();
  const response = await api.get('/enthusiast/skills');
  return response.data;
};

const addSkill = async (skill_name) => {
  const api = createAuthAxios();
  const response = await api.post('/enthusiast/skills', { skill_name });
  return response.data;
};

const deleteSkill = async (skill_id) => {
  const api = createAuthAxios();
  const response = await api.delete(`/enthusiast/skills/${skill_id}`);
  return response.data;
};

// Showreel Management
const getShowreel = async () => {
  const api = createAuthAxios();
  const response = await api.get('/enthusiast/showreel');
  return response.data;
};

const createUpdateShowreel = async (showreelData) => {
  const api = createAuthAxios();
  const response = await api.post('/enthusiast/showreel', showreelData);
  return response.data;
};

// Notifications
const getNotifications = async (limit = 10) => {
  const api = createAuthAxios();
  const response = await api.get(`/enthusiast/notifications?limit=${limit}`);
  return response.data;
};

const markNotificationRead = async (notification_id) => {
  const api = createAuthAxios();
  const response = await api.put(`/enthusiast/notifications/${notification_id}/read`);
  return response.data;
};

const markAllNotificationsRead = async () => {
  const api = createAuthAxios();
  const response = await api.put('/enthusiast/notifications/mark-all-read');
  return response.data;
};

// Projects
const getProjects = async () => {
  const api = createAuthAxios();
  const response = await api.get('/enthusiast/projects');
  return response.data;
};

const createProject = async (projectData) => {
  const api = createAuthAxios();
  const response = await api.post('/enthusiast/projects', projectData);
  return response.data;
};

// Production House API Calls
const productionApi = {
  verifyCode: async (code) => {
    const api = createAuthAxios();
    const response = await api.post('/production/verify-code', { code });
    return response.data;
  },

  apply: async (code) => {
    const api = createAuthAxios();
    const response = await api.post('/production/apply', {
      code: code,
      consent_to_share_data: true
    });
    return response.data;
  },

  getConnection: async () => {
    const api = createAuthAxios();
    const response = await api.get('/production/connection');
    return response.data;
  },

  getPendingInvitations: async () => {
    const api = createAuthAxios();
    const response = await api.get('/production/invitations/pending');
    return response.data;
  },

  acceptInvitation: async (invitationId) => {
    const api = createAuthAxios();
    const response = await api.post(`/production/invitations/${invitationId}/accept`);
    return response.data;
  },

  applyLeave: async (leaveData) => {
    const api = createAuthAxios();
    const response = await api.post('/production/leaves/apply', leaveData);
    return response.data;
  },

  submitComplaint: async (complaintData) => {
    const api = createAuthAxios();
    const response = await api.post('/production/complaints', complaintData);
    return response.data;
  },

  submitFeedback: async (feedbackData) => {
    const api = createAuthAxios();
    const response = await api.post('/production/feedback', feedbackData);
    return response.data;
  },

  leaveProduction: async () => {
    const api = createAuthAxios();
    const response = await api.post('/production/leave');
    return response.data;
  }
};

// ==================== UI COMPONENTS ====================

// Enhanced Card Component with teal theme
const Card = ({ children, className = "", id = "" }) => (
  <div
    id={id}
    className={`film-enthusiast-card bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border-2 border-teal-100 hover:border-teal-300 ${className}`}
    role="region"
  >
    {children}
  </div>
);

const CardHeader = ({ children }) => (
  <div className="film-enthusiast-card-header mb-4 pb-3 border-b-2 border-teal-100">
    {children}
  </div>
);

const CardTitle = ({ children }) => (
  <h2 className="film-enthusiast-card-title text-xl font-bold text-gray-900 flex items-center gap-2">
    {children}
  </h2>
);

const CardContent = ({ children }) => (
  <div className="film-enthusiast-card-content">
    {children}
  </div>
);

// Enhanced Alert Component with teal theme
const Alert = ({ children }) => (
  <div className="film-enthusiast-alert bg-gradient-to-r from-teal-50 to-cyan-50 border-l-4 border-teal-500 text-gray-900 p-5 mt-6 rounded-r-lg shadow-md" role="alert">
    <div className="flex items-start gap-3">
      <Sparkles className="text-teal-500 mt-1 flex-shrink-0" size={20} />
      <div className="flex-1">{children}</div>
    </div>
  </div>
);

const AlertTitle = ({ children }) => (
  <h4 className="film-enthusiast-alert-title text-lg font-bold mb-2 text-gray-900">
    {children}
  </h4>
);

const AlertDescription = ({ children }) => (
  <p className="film-enthusiast-alert-description text-gray-800 font-medium">
    {children}
  </p>
);

// Loading fallback component
const LoadingFallback = () => (
  <div className="flex justify-center items-center h-64">
    <div className="relative">
      <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-teal-500"></div>
      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
        <Film className="text-teal-500" size={24} />
      </div>
    </div>
  </div>
);

// ==================== PRODUCTION HOUSE COMPONENTS ====================

// Production House Linking Card Component
const ProductionHouseLinkingCard = ({ onConnectionUpdate }) => {
  const [activeTab, setActiveTab] = useState('code');
  const [invitationCode, setInvitationCode] = useState('');
  const [verifiedHouse, setVerifiedHouse] = useState(null);
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showConsentDialog, setShowConsentDialog] = useState(false);
  const [loadingInvitations, setLoadingInvitations] = useState(false);

  // Fetch pending invitations when tab changes
  useEffect(() => {
    if (activeTab === 'invitation') {
      fetchPendingInvitations();
    }
  }, [activeTab]);

  const fetchPendingInvitations = async () => {
    setLoadingInvitations(true);
    try {
      const invitations = await productionApi.getPendingInvitations();
      setPendingInvitations(invitations || []);
    } catch (err) {
      setPendingInvitations([]);
    } finally {
      setLoadingInvitations(false);
    }
  };

  const verifyCode = async () => {
    setError('');
    setSuccess('');
    setVerifiedHouse(null);

    if (!invitationCode.trim()) {
      setError('Please enter an invitation code');
      return;
    }

    setLoading(true);
    try {
      const houseData = await productionApi.verifyCode(invitationCode.trim());
      setVerifiedHouse(houseData);
      setSuccess('Code verified successfully!');
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired invitation code');
    } finally {
      setLoading(false);
    }
  };

  const applyToProductionHouse = async () => {
    if (!verifiedHouse) return;

    setLoading(true);
    setError('');
    try {
      await productionApi.apply(invitationCode.trim());

      setSuccess('Application submitted successfully! Waiting for approval.');
      setShowConsentDialog(false);
      setVerifiedHouse(null);
      setInvitationCode('');

      setTimeout(() => {
        onConnectionUpdate();
      }, 2000);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        setError(detail.map(e => e.msg).join(', '));
      } else {
        setError(detail || 'Failed to submit application');
      }
    } finally {
      setLoading(false);
    }
  };

  const acceptInvitation = async (invitationId) => {
    setLoading(true);
    setError('');
    try {
      await productionApi.acceptInvitation(invitationId);

      setSuccess('Invitation accepted! Waiting for approval.');
      fetchPendingInvitations();

      setTimeout(() => {
        onConnectionUpdate();
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to accept invitation');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="production-house-link-card bg-gradient-to-br from-teal-50 via-white to-cyan-50 border-2 border-teal-300 shadow-xl">
      <CardHeader>
        <CardTitle>
          <Building2 className="text-teal-600" size={28} />
          <span className="text-gray-900 font-bold">Connect to Production House</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="production-house-description text-gray-700 mb-6 font-medium">
          Join a production house to access exclusive projects, tools, and team collaboration features.
        </p>

        <div className="production-house-tabs-wrapper flex gap-3 mb-6">
          <button
            onClick={() => setActiveTab('code')}
            style={activeTab === 'code'
              ? { backgroundColor: '#0d9488', color: '#ffffff' }
              : { backgroundColor: '#ffffff', color: '#0f766e' }
            }
            className={`production-house-tab-btn flex-1 py-3 px-4 rounded-lg font-semibold transition-all duration-300 border-2 ${activeTab === 'code'
              ? 'border-teal-600 shadow-lg transform scale-105'
              : 'border-teal-300 hover:border-teal-500 hover:bg-teal-50'
              }`}
          >
            <span className="flex items-center justify-center gap-2">
              <Building2 size={18} />
              Enter Code
            </span>
          </button>
          <button
            onClick={() => setActiveTab('invitation')}
            style={activeTab === 'invitation'
              ? { backgroundColor: '#0d9488', color: '#ffffff' }
              : { backgroundColor: '#ffffff', color: '#0f766e' }
            }
            className={`production-house-tab-btn flex-1 py-3 px-4 rounded-lg font-semibold transition-all duration-300 border-2 ${activeTab === 'invitation'
              ? 'border-teal-600 shadow-lg transform scale-105'
              : 'border-teal-300 hover:border-teal-500 hover:bg-teal-50'
              }`}
          >
            <span className="flex items-center justify-center gap-2">
              <Bell size={18} />
              Accept Invitation
            </span>
          </button>
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div className="production-house-error-msg mb-4 p-4 bg-red-50 border-l-4 border-red-500 text-red-900 rounded flex items-start gap-2 shadow-sm">
            <AlertTriangle size={20} className="flex-shrink-0 mt-0.5" />
            <span className="font-medium">{error}</span>
          </div>
        )}
        {success && (
          <div className="production-house-success-msg mb-4 p-4 bg-green-50 border-l-4 border-green-500 text-green-900 rounded flex items-start gap-2 shadow-sm">
            <CheckCircle size={20} className="flex-shrink-0 mt-0.5" />
            <span className="font-medium">{success}</span>
          </div>
        )}

        {/* Enter Code Tab */}
        {activeTab === 'code' && (
          <div className="production-house-code-tab space-y-4">
            <div>
              <label className="block text-sm font-bold text-gray-800 mb-2">
                Invitation Code
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={invitationCode}
                  onChange={(e) => setInvitationCode(e.target.value.toUpperCase())}
                  placeholder="Enter 10-character code"
                  maxLength={10}
                  className="production-house-code-input flex-1 px-4 py-3 border-2 border-teal-300 rounded-lg focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none text-gray-900 font-mono bg-white shadow-sm placeholder-gray-400"
                  disabled={loading}
                />
                <button
                  onClick={verifyCode}
                  disabled={loading || !invitationCode.trim()}
                  style={{ backgroundColor: '#0d9488', color: '#ffffff' }}
                  className="production-house-verify-btn px-6 py-3 rounded-lg hover:opacity-90 active:opacity-80 disabled:opacity-40 disabled:cursor-not-allowed font-bold shadow-md flex items-center gap-2 transition-all duration-200 border-2 border-teal-700 min-w-[100px] justify-center"
                >
                  {loading ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    'Verify'
                  )}
                </button>
              </div>
            </div>

            {/* Verified Production House Info */}
            {verifiedHouse && (
              <div className="production-house-verified-card mt-6 p-6 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-lg border-2 border-emerald-400 shadow-lg">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">{verifiedHouse.production_house_name}</h3>
                    {verifiedHouse.location && (
                      <p className="text-gray-700 mt-1 font-medium">{verifiedHouse.location}</p>
                    )}
                  </div>
                  <CheckCircle className="text-emerald-500" size={32} />
                </div>

                {verifiedHouse.role_offered && (
                  <div className="mb-4">
                    <span className="text-sm font-bold text-gray-800">Role Offered:</span>
                    <span className="ml-2 px-3 py-1 bg-teal-100 text-teal-900 rounded-full text-sm font-bold">
                      {verifiedHouse.role_offered}
                    </span>
                  </div>
                )}

                <button
                  onClick={() => setShowConsentDialog(true)}
                  disabled={loading}
                  style={{ backgroundColor: '#059669', color: '#ffffff' }}
                  className="production-house-apply-btn w-full py-3 rounded-lg hover:opacity-90 active:opacity-80 font-bold shadow-md disabled:opacity-50 transition-all duration-200"
                >
                  Apply Now
                </button>
              </div>
            )}
          </div>
        )}

        {/* Accept Invitation Tab */}
        {activeTab === 'invitation' && (
          <div className="production-house-invitation-tab">
            {loadingInvitations ? (
              <div className="text-center py-8">
                <Loader2 className="mx-auto mb-3 text-teal-500 animate-spin" size={48} />
                <p className="text-gray-700 font-medium">Loading invitations...</p>
              </div>
            ) : pendingInvitations.length === 0 ? (
              <div className="text-center py-8 text-gray-700">
                <Bell className="mx-auto mb-3 text-teal-400" size={48} />
                <p className="font-bold text-gray-900">No pending invitations</p>
                <p className="text-sm mt-1 text-gray-600">Ask your production house to send you an invitation</p>
              </div>
            ) : (
              <div className="space-y-4">
                {pendingInvitations.map((invitation) => (
                  <div key={invitation.invitation_id} className="production-house-invitation-item p-4 bg-white rounded-lg border-2 border-teal-200 shadow hover:border-teal-400 hover:shadow-lg transition-all">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-bold text-gray-900">{invitation.production_house_name}</h3>
                        {invitation.location && (
                          <p className="text-sm text-gray-700 mt-1 font-medium">{invitation.location}</p>
                        )}
                        {invitation.role_offered && (
                          <span className="inline-block mt-2 px-3 py-1 bg-teal-100 text-teal-900 rounded-full text-sm font-bold">
                            {invitation.role_offered}
                          </span>
                        )}
                        {invitation.expires_at && (
                          <p className="text-xs text-gray-600 mt-2">
                            Expires: {new Date(invitation.expires_at).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => acceptInvitation(invitation.invitation_id)}
                        disabled={loading}
                        style={{ backgroundColor: '#059669', color: '#ffffff' }}
                        className="production-house-accept-btn px-4 py-2 rounded-lg hover:opacity-90 active:opacity-80 disabled:opacity-50 font-bold flex items-center gap-2 shadow-md transition-all duration-200 border-2 border-emerald-700"
                      >
                        {loading ? (
                          <>
                            <Loader2 size={16} className="animate-spin" />
                            Accepting...
                          </>
                        ) : (
                          'Accept'
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Consent Dialog */}
        {showConsentDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="production-house-consent-dialog bg-white rounded-xl p-6 max-w-md w-full shadow-2xl border-2 border-teal-300">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Data Sharing Consent</h3>
              <p className="text-gray-700 mb-6 font-medium">
                By applying to <strong className="text-teal-700">{verifiedHouse?.production_house_name}</strong>, you agree to share:
              </p>
              <ul className="mb-6 space-y-2 text-gray-700">
                <li className="flex items-start gap-2">
                  <CheckCircle size={20} className="text-teal-500 flex-shrink-0 mt-0.5" />
                  <span className="font-medium">Your profile information (name, email, phone)</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={20} className="text-teal-500 flex-shrink-0 mt-0.5" />
                  <span className="font-medium">Your skills and specialization</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={20} className="text-teal-500 flex-shrink-0 mt-0.5" />
                  <span className="font-medium">Your showreel and portfolio</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={20} className="text-teal-500 flex-shrink-0 mt-0.5" />
                  <span className="font-medium">Project work and collaboration history</span>
                </li>
              </ul>
              <p className="text-sm text-gray-700 mb-6 p-3 bg-amber-50 border-l-4 border-amber-400 rounded font-medium">
                This information will be visible to production house administrators and team members.
              </p>
              <div className="flex flex-col gap-3">
                <button
                  onClick={applyToProductionHouse}
                  disabled={loading}
                  style={{ backgroundColor: '#0d9488', color: '#ffffff' }}
                  className="production-house-consent-btn w-full py-3 rounded-lg hover:opacity-90 active:opacity-80 disabled:opacity-50 font-bold flex items-center justify-center gap-2 shadow-md transition-all duration-200"
                >
                  {loading ? (
                    <><Loader2 size={18} className="animate-spin" /> Submitting...</>
                  ) : (
                    'I Agree & Apply'
                  )}
                </button>
                <button
                  onClick={() => setShowConsentDialog(false)}
                  disabled={loading}
                  className="production-house-cancel-btn w-full py-3 bg-gray-100 text-gray-800 rounded-lg hover:bg-gray-200 font-bold disabled:opacity-50 transition-all duration-200 border-2 border-gray-300"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Production Menu Component - WITH DEBUG LOGS AND LOGO DISPLAY
const ProductionMenu = ({ productionHouse, onLeaveProduction }) => {
  // ✅ DEBUG CONSOLE LOGS - CHECK WHAT DATA WE'RE RECEIVING
  const [activeModal, setActiveModal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Leave form state
  const [leaveForm, setLeaveForm] = useState({
    leave_type: 'vacation',
    start_date: '',
    end_date: '',
    reason: ''
  });

  // Complaint form state
  const [complaintForm, setComplaintForm] = useState({
    complaint_type: 'technical',
    priority: 'medium',
    subject: '',
    description: ''
  });

  // Feedback form state
  const [feedbackForm, setFeedbackForm] = useState({
    rating: 5,
    category: 'general',
    feedback_text: '',
    is_anonymous: false
  });

  const resetForms = () => {
    setLeaveForm({ leave_type: 'vacation', start_date: '', end_date: '', reason: '' });
    setComplaintForm({ complaint_type: 'technical', priority: 'medium', subject: '', description: '' });
    setFeedbackForm({ rating: 5, category: 'general', feedback_text: '', is_anonymous: false });
    setError('');
    setSuccess('');
  };

  const submitLeave = async () => {
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      await productionApi.applyLeave(leaveForm);
      setSuccess('Leave application submitted successfully!');
      setTimeout(() => {
        setActiveModal(null);
        resetForms();
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit leave application');
    } finally {
      setLoading(false);
    }
  };

  const submitComplaint = async () => {
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      await productionApi.submitComplaint(complaintForm);
      setSuccess('Complaint submitted successfully! We will review it shortly.');
      setTimeout(() => {
        setActiveModal(null);
        resetForms();
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit complaint');
    } finally {
      setLoading(false);
    }
  };

  const submitFeedback = async () => {
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      await productionApi.submitFeedback(feedbackForm);
      setSuccess('Thank you for your feedback!');
      setTimeout(() => {
        setActiveModal(null);
        resetForms();
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit feedback');
    } finally {
      setLoading(false);
    }
  };

  const handleLeaveProduction = async () => {
    if (!window.confirm(`Are you sure you want to leave ${productionHouse.production_house?.name || 'this production house'}? This action cannot be undone.`)) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      await productionApi.leaveProduction();
      alert('You have successfully left the production house.');
      onLeaveProduction();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to leave production house');
      alert('Failed to leave production house. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const menuItems = [
    { id: 'leaves', icon: Calendar, label: 'Apply for Leave', bgColor: '#0d9488' },
    { id: 'scripts', icon: FileText, label: 'Access Scripts', bgColor: '#0891b2' },
    { id: 'tools', icon: BsTools, label: 'Request Tools', bgColor: '#059669' },
    { id: 'projects', icon: Film, label: 'Production Projects', bgColor: '#2563eb' },
    { id: 'team', icon: Users, label: 'Team Directory', bgColor: '#4f46e5' },
    { id: 'announcements', icon: Megaphone, label: 'Announcements', bgColor: '#7c3aed' },
  ];

  const settingsItems = [
    { id: 'complaint', icon: AlertTriangle, label: 'Raise Complaint', iconColor: 'text-red-500' },
    { id: 'feedback', icon: MessageSquare, label: 'Submit Feedback', iconColor: 'text-teal-600' },
    { id: 'technical', icon: Settings, label: 'Technical Issues', iconColor: 'text-gray-500' },
    { id: 'leave-production', icon: LogOut, label: 'Leave Production', iconColor: 'text-red-500' },
  ];

  return (
    <div className="production-menu-wrapper space-y-6">
      {/* ✅ UPDATED: Production House Header with Logo */}
      <Card className="production-menu-badge-card bg-gradient-to-br from-teal-50 via-cyan-50 to-teal-50 border-2 border-teal-400 shadow-xl">
        <div className="flex items-center gap-4">
          {/* ✅ Production House Logo - FIXED: Use R2_PUBLIC_URL/img/ pattern */}
          {productionHouse.production_house?.logo_url ? (
            <div className="w-20 h-20 rounded-xl overflow-hidden shadow-lg border-2 border-teal-300 flex-shrink-0 bg-white">
              <img
                src={`${R2_PUBLIC_URL}/img/${productionHouse.production_house.logo_url}`}
                alt={`${productionHouse.production_house.name} logo`}
                className="w-full h-full object-cover"
                onError={(e) => {
                  // Fallback to icon if image fails to load
                  e.target.style.display = 'none';
                  const fallback = document.createElement('div');
                  fallback.style.cssText = 'width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; background-color: #0d9488;';
                  fallback.innerHTML = '<svg class="text-white" width="36" height="36" fill="currentColor" viewBox="0 0 24 24"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path><polyline points="9 22 9 12 15 12 15 22"></polyline></svg>';
                  e.target.parentElement.appendChild(fallback);
                }}
              />
            </div>
          ) : (
            <div style={{ backgroundColor: '#0d9488' }} className="w-20 h-20 rounded-xl shadow-lg flex items-center justify-center flex-shrink-0">
              <Building2 className="text-white" size={36} />
            </div>
          )}

          {/* ✅ Production House Name & Info - FIXED: Access nested production_house fields */}
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-gray-900 mb-1">
              {productionHouse.production_house?.name || 'Production House'}
            </h2>
            {productionHouse.production_house?.location && (
              <p className="text-gray-700 font-medium flex items-center gap-1 mb-2">
                <Building2 size={16} className="text-teal-600" />
                {productionHouse.production_house.location}
              </p>
            )}
            {productionHouse.role_in_production && (
              <span className="inline-block px-3 py-1 bg-teal-100 text-teal-800 rounded-full text-sm font-bold">
                Role: {productionHouse.role_in_production}
              </span>
            )}
          </div>
        </div>
      </Card>

      {/* ✅ UPDATED: Main Features Grid - Perfect Alignment */}
      <div className="production-menu-features-grid grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {menuItems.map((item) => (
          <button
            key={item.id}
            onClick={() => setActiveModal(item.id)}
            style={{ backgroundColor: item.bgColor, color: '#ffffff' }}
            className="production-menu-feature-btn p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 hover:opacity-90 hover:scale-105 active:opacity-80 flex flex-col items-center justify-center text-center gap-3 min-h-[140px]"
          >
            <item.icon size={36} className="flex-shrink-0" />
            <span className="font-bold text-base leading-tight">{item.label}</span>
          </button>
        ))}
      </div>

      {/* Settings Section */}
      <Card className="production-menu-settings-card">
        <CardHeader>
          <CardTitle>
            <Settings className="text-teal-600" size={24} />
            <span className="text-gray-900">Settings & Support</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="production-menu-settings-grid grid grid-cols-1 md:grid-cols-2 gap-3">
            {settingsItems.map((item) => (
              <button
                key={item.id}
                onClick={() => item.id === 'leave-production' ? handleLeaveProduction() : setActiveModal(item.id)}
                disabled={loading && item.id === 'leave-production'}
                className="production-menu-setting-btn p-4 bg-white hover:bg-teal-50 rounded-lg transition-all flex items-center gap-3 text-gray-900 font-medium disabled:opacity-50 border-2 border-gray-200 hover:border-teal-300 shadow-sm hover:shadow-md"
              >
                <item.icon size={20} className={item.iconColor} />
                <span>{item.label}</span>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Leave Application Modal */}
      {activeModal === 'leaves' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="production-menu-leave-modal bg-white rounded-xl p-6 max-w-md w-full shadow-2xl max-h-[90vh] overflow-y-auto border-2 border-teal-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900">Apply for Leave</h3>
              <button onClick={() => { setActiveModal(null); resetForms(); }} className="text-gray-500 hover:text-gray-700 p-1 rounded-full hover:bg-gray-100">
                <X size={24} />
              </button>
            </div>

            {error && <div className="mb-4 p-3 bg-red-50 border-l-4 border-red-500 text-red-900 text-sm rounded flex items-start gap-2 font-medium">
              <AlertTriangle size={18} className="flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>}
            {success && <div className="mb-4 p-3 bg-green-50 border-l-4 border-green-500 text-green-900 text-sm rounded flex items-start gap-2 font-medium">
              <CheckCircle size={18} className="flex-shrink-0 mt-0.5" />
              <span>{success}</span>
            </div>}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-gray-800 mb-1">Leave Type</label>
                <select
                  value={leaveForm.leave_type}
                  onChange={(e) => setLeaveForm({ ...leaveForm, leave_type: e.target.value })}
                  className="w-full px-3 py-2 bg-white border-2 border-teal-300 rounded-lg focus:border-teal-500 outline-none text-gray-900 font-medium"
                >
                  <option value="sick">Sick Leave</option>
                  <option value="vacation">Vacation</option>
                  <option value="personal">Personal</option>
                  <option value="emergency">Emergency</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-800 mb-1">Start Date</label>
                <input
                  type="date"
                  value={leaveForm.start_date}
                  onChange={(e) => setLeaveForm({ ...leaveForm, start_date: e.target.value })}
                  className="w-full px-3 py-2 bg-white border-2 border-teal-300 rounded-lg focus:border-teal-500 outline-none text-gray-900"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-800 mb-1">End Date</label>
                <input
                  type="date"
                  value={leaveForm.end_date}
                  onChange={(e) => setLeaveForm({ ...leaveForm, end_date: e.target.value })}
                  className="w-full px-3 py-2 bg-white border-2 border-teal-300 rounded-lg focus:border-teal-500 outline-none text-gray-900"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-800 mb-1">Reason (Optional)</label>
                <textarea
                  value={leaveForm.reason}
                  onChange={(e) => setLeaveForm({ ...leaveForm, reason: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 bg-white border-2 border-teal-300 rounded-lg focus:border-teal-500 outline-none text-gray-900"
                  placeholder="Explain your reason for leave..."
                />
              </div>

              <button
                onClick={submitLeave}
                disabled={loading || !leaveForm.start_date || !leaveForm.end_date}
                style={{ backgroundColor: '#0d9488', color: '#ffffff' }}
                className="w-full py-3 rounded-lg hover:opacity-90 active:opacity-80 disabled:opacity-40 disabled:cursor-not-allowed font-bold flex items-center justify-center gap-2 shadow-md border-2 border-teal-700"
              >
                {loading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send size={18} />
                    Submit Leave Application
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Complaint Modal */}
      {activeModal === 'complaint' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="production-menu-complaint-modal bg-white rounded-xl p-6 max-w-md w-full shadow-2xl max-h-[90vh] overflow-y-auto border-2 border-teal-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900">Raise Complaint</h3>
              <button onClick={() => { setActiveModal(null); resetForms(); }} className="text-gray-500 hover:text-gray-700 p-1 rounded-full hover:bg-gray-100">
                <X size={24} />
              </button>
            </div>

            {error && <div className="mb-4 p-3 bg-red-50 border-l-4 border-red-500 text-red-900 text-sm rounded flex items-start gap-2 font-medium">
              <AlertTriangle size={18} className="flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>}
            {success && <div className="mb-4 p-3 bg-green-50 border-l-4 border-green-500 text-green-900 text-sm rounded flex items-start gap-2 font-medium">
              <CheckCircle size={18} className="flex-shrink-0 mt-0.5" />
              <span>{success}</span>
            </div>}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-gray-800 mb-1">Complaint Type</label>
                <select
                  value={complaintForm.complaint_type}
                  onChange={(e) => setComplaintForm({ ...complaintForm, complaint_type: e.target.value })}
                  className="w-full px-3 py-2 bg-white border-2 border-teal-300 rounded-lg focus:border-teal-500 outline-none text-gray-900 font-medium"
                >
                  <option value="technical">Technical Issue</option>
                  <option value="hr">HR Related</option>
                  <option value="equipment">Equipment</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-800 mb-1">Priority</label>
                <select
                  value={complaintForm.priority}
                  onChange={(e) => setComplaintForm({ ...complaintForm, priority: e.target.value })}
                  className="w-full px-3 py-2 bg-white border-2 border-teal-300 rounded-lg focus:border-teal-500 outline-none text-gray-900 font-medium"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-800 mb-1">Subject</label>
                <input
                  type="text"
                  value={complaintForm.subject}
                  onChange={(e) => setComplaintForm({ ...complaintForm, subject: e.target.value })}
                  className="w-full px-3 py-2 bg-white border-2 border-teal-300 rounded-lg focus:border-teal-500 outline-none text-gray-900"
                  placeholder="Brief subject of complaint"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-800 mb-1">Description</label>
                <textarea
                  value={complaintForm.description}
                  onChange={(e) => setComplaintForm({ ...complaintForm, description: e.target.value })}
                  rows={4}
                  className="w-full px-3 py-2 bg-white border-2 border-teal-300 rounded-lg focus:border-teal-500 outline-none text-gray-900"
                  placeholder="Describe your complaint in detail..."
                />
              </div>

              <button
                onClick={submitComplaint}
                disabled={loading || !complaintForm.subject || !complaintForm.description}
                style={{ backgroundColor: '#dc2626', color: '#ffffff' }}
                className="w-full py-3 rounded-lg hover:opacity-90 active:opacity-80 disabled:opacity-40 disabled:cursor-not-allowed font-bold flex items-center justify-center gap-2 shadow-md border-2 border-red-700"
              >
                {loading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send size={18} />
                    Submit Complaint
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Feedback Modal */}
      {activeModal === 'feedback' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="production-menu-feedback-modal bg-white rounded-xl p-6 max-w-md w-full shadow-2xl max-h-[90vh] overflow-y-auto border-2 border-teal-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900">Submit Feedback</h3>
              <button onClick={() => { setActiveModal(null); resetForms(); }} className="text-gray-500 hover:text-gray-700 p-1 rounded-full hover:bg-gray-100">
                <X size={24} />
              </button>
            </div>

            {error && <div className="mb-4 p-3 bg-red-50 border-l-4 border-red-500 text-red-900 text-sm rounded flex items-start gap-2 font-medium">
              <AlertTriangle size={18} className="flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>}
            {success && <div className="mb-4 p-3 bg-green-50 border-l-4 border-green-500 text-green-900 text-sm rounded flex items-start gap-2 font-medium">
              <CheckCircle size={18} className="flex-shrink-0 mt-0.5" />
              <span>{success}</span>
            </div>}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-gray-800 mb-2">Rating</label>
                <div className="flex gap-2 justify-center">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      onClick={() => setFeedbackForm({ ...feedbackForm, rating: star })}
                      className="focus:outline-none transition-transform hover:scale-110"
                    >
                      <Star
                        size={32}
                        className={star <= feedbackForm.rating ? 'fill-teal-500 text-teal-500' : 'text-gray-300'}
                      />
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-800 mb-1">Category</label>
                <select
                  value={feedbackForm.category}
                  onChange={(e) => setFeedbackForm({ ...feedbackForm, category: e.target.value })}
                  className="w-full px-3 py-2 bg-white border-2 border-teal-300 rounded-lg focus:border-teal-500 outline-none text-gray-900 font-medium"
                >
                  <option value="general">General</option>
                  <option value="management">Management</option>
                  <option value="projects">Projects</option>
                  <option value="environment">Work Environment</option>
                  <option value="tools">Tools & Equipment</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-800 mb-1">Your Feedback</label>
                <textarea
                  value={feedbackForm.feedback_text}
                  onChange={(e) => setFeedbackForm({ ...feedbackForm, feedback_text: e.target.value })}
                  rows={4}
                  className="w-full px-3 py-2 bg-white border-2 border-teal-300 rounded-lg focus:border-teal-500 outline-none text-gray-900"
                  placeholder="Share your thoughts and suggestions..."
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="anonymous"
                  checked={feedbackForm.is_anonymous}
                  onChange={(e) => setFeedbackForm({ ...feedbackForm, is_anonymous: e.target.checked })}
                  className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                />
                <label htmlFor="anonymous" className="text-sm text-gray-700 font-medium">
                  Submit anonymously
                </label>
              </div>

              <button
                onClick={submitFeedback}
                disabled={loading || !feedbackForm.feedback_text}
                style={{ backgroundColor: '#0d9488', color: '#ffffff' }}
                className="w-full py-3 rounded-lg hover:opacity-90 active:opacity-80 disabled:opacity-40 disabled:cursor-not-allowed font-bold flex items-center justify-center gap-2 shadow-md border-2 border-teal-700"
              >
                {loading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send size={18} />
                    Submit Feedback
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Placeholder modals for other features */}
      {['scripts', 'tools', 'projects', 'team', 'announcements', 'technical'].includes(activeModal) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="production-menu-placeholder-modal bg-white rounded-xl p-6 max-w-md w-full shadow-2xl border-2 border-teal-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900 capitalize">{activeModal.replace('-', ' ')}</h3>
              <button onClick={() => setActiveModal(null)} className="text-gray-500 hover:text-gray-700 p-1 rounded-full hover:bg-gray-100">
                <X size={24} />
              </button>
            </div>
            <div className="text-center py-8">
              <Sparkles className="mx-auto mb-3 text-teal-500" size={48} />
              <p className="text-gray-900 font-bold mb-2">Coming Soon!</p>
              <p className="text-gray-600 text-sm font-medium">
                This feature is under development and will be available soon.
              </p>
            </div>
            <button
              onClick={() => setActiveModal(null)}
              className="w-full py-3 bg-gray-100 text-gray-800 rounded-lg hover:bg-gray-200 active:bg-gray-300 font-bold mt-4 transition-all border-2 border-gray-300"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== END PRODUCTION HOUSE COMPONENTS ====================

// Skills showcase component
const SkillsShowcase = ({ skills, setSkills, onSkillsChange }) => {
  const [newSkill, setNewSkill] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [skillsData, setSkillsData] = useState([]);

  // Fetch full skills data when component mounts
  useEffect(() => {
    fetchSkillsData();
  }, [skills]);

  const fetchSkillsData = async () => {
    try {
      const data = await getSkills();
      setSkillsData(data);
    } catch (err) {
    }
  };

  const addSkillHandler = async () => {
    if (!newSkill.trim() || skills.includes(newSkill.trim())) {
      return;
    }

    setLoading(true);
    setError('');
    try {
      await addSkill(newSkill.trim());

      // Update local state
      const updatedSkills = [...skills, newSkill.trim()];
      setSkills(updatedSkills);
      setNewSkill('');

      // Notify parent to refresh dashboard
      if (onSkillsChange) {
        onSkillsChange();
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to add skill');
    } finally {
      setLoading(false);
    }
  };

  const deleteSkillHandler = async (skillToDelete) => {
    setLoading(true);
    setError('');
    try {
      // Find the skill ID from the backend data
      const skillToDeleteData = skillsData.find(s => s.skill_name === skillToDelete);

      if (skillToDeleteData) {
        await deleteSkill(skillToDeleteData.skill_id);
      }

      // Update local state
      const updatedSkills = skills.filter(skill => skill !== skillToDelete);
      setSkills(updatedSkills);

      // Notify parent to refresh dashboard
      if (onSkillsChange) {
        onSkillsChange();
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete skill');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      addSkillHandler();
    }
  };

  return (
    <div className="skills-showcase-container-wrapper">
      {error && (
        <div className="skills-showcase-error text-red-900 text-sm mb-2 p-2 bg-red-50 rounded font-medium">{error}</div>
      )}

      <div className="skills-showcase-tags-container flex flex-wrap gap-2">
        {skills.map((skill) => (
          <span
            key={skill}
            style={{ backgroundColor: '#0d9488', color: '#ffffff' }}
            className="skills-showcase-skill-tag px-4 py-2 rounded-full text-sm font-bold flex items-center shadow-md hover:shadow-lg transition-all duration-200 hover:scale-105"
          >
            {skill}
            <button
              onClick={() => deleteSkillHandler(skill)}
              disabled={loading}
              className="skills-showcase-skill-delete-btn ml-2 text-white hover:text-red-200 focus:outline-none transition-colors disabled:opacity-50 font-bold"
              aria-label={`Remove ${skill} skill`}
            >
              ×
            </button>
          </span>
        ))}
      </div>

      <div className="skills-showcase-input-wrapper mt-4 flex items-center gap-2">
        <input
          type="text"
          value={newSkill}
          onChange={(e) => setNewSkill(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Add a skill"
          disabled={loading}
          className="skills-showcase-skill-input bg-white border-2 border-teal-200 rounded-lg px-4 py-2 flex-1 focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none transition-all text-gray-900 disabled:opacity-50 font-medium placeholder-gray-400"
          aria-label="Add a new skill"
        />
        <button
          onClick={addSkillHandler}
          disabled={loading || !newSkill.trim()}
          style={{ backgroundColor: '#0d9488', color: '#ffffff' }}
          className="skills-showcase-add-skill-btn px-6 py-2 rounded-lg hover:opacity-90 active:opacity-80 focus:ring-2 focus:ring-teal-400 focus:outline-none transition-all duration-200 font-bold shadow-md hover:shadow-lg disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2 border-2 border-teal-700"
          aria-label="Add skill"
        >
          {loading ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              Adding...
            </>
          ) : (
            'Add'
          )}
        </button>
      </div>
    </div>
  );
};

// Showreel component
const Showreel = ({ showReel, toggleShowReel }) => {
  return (
    <div className="showreel-component-wrapper">
      <button
        onClick={toggleShowReel}
        id="showreel-toggle-btn"
        style={{ backgroundColor: '#0d9488', color: '#ffffff' }}
        className="showreel-toggle-btn mt-4 px-6 py-2 rounded-lg hover:opacity-90 active:opacity-80 focus:ring-2 focus:ring-teal-400 focus:outline-none transition-all duration-200 font-bold shadow-md hover:shadow-lg border-2 border-teal-700"
        aria-expanded={showReel}
      >
        {showReel ? 'Hide Showreel' : 'View Showreel'}
      </button>

      {showReel && (
        <div className="showreel-display-container mt-4 rounded-lg overflow-hidden shadow-lg border-2 border-teal-200">
          <img
            src="/api/placeholder/640/360"
            alt="Showreel placeholder"
            className="showreel-display-img w-full h-auto hover:scale-105 transition-transform duration-300"
          />
        </div>
      )}
    </div>
  );
};

// Dashboard content component
const DashboardContent = ({
  skills,
  setSkills,
  dashboardData,
  showReel,
  toggleShowReel,
  setCurrentComponent,
  onSkillsChange
}) => (
  <>
    <div className="dashboard-content-grid grid grid-cols-1 md:grid-cols-3 gap-6">
      {/* Project Activity Chart - Real Data from Backend */}
      <Card className="dashboard-project-activity-card" id="project-activity-card">
        <CardHeader>
          <CardTitle>
            <Award className="text-teal-500" size={24} />
            <span className="text-gray-900">Project Activity (Last 6 Months)</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {dashboardData.project_activity && dashboardData.project_activity.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={dashboardData.project_activity}>
                <defs>
                  <linearGradient id="colorProjects" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#14b8a6" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#14b8a6" stopOpacity={0.1} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="name" stroke="#374151" style={{ fontSize: '12px' }} />
                <YAxis stroke="#374151" style={{ fontSize: '12px' }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#0f766e',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#fff',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}
                />
                <Legend wrapperStyle={{ fontSize: '12px', paddingTop: '10px' }} />
                <Line
                  type="monotone"
                  dataKey="projects"
                  stroke="#14b8a6"
                  strokeWidth={3}
                  dot={{ fill: '#14b8a6', r: 5 }}
                  activeDot={{ r: 7 }}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center text-gray-700 py-8">
              <Award className="mx-auto mb-2 text-teal-400" size={40} />
              <p className="font-bold text-gray-900">No project activity yet</p>
              <p className="text-sm mt-1 text-gray-600">Start working on projects to see your activity here!</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Notifications - Real Data from Backend */}
      <Card className="dashboard-notifications-card" id="notifications-card">
        <CardHeader>
          <CardTitle>
            <Bell className="text-teal-500" size={24} />
            <span className="text-gray-900">Recent Notifications</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {dashboardData.notifications && dashboardData.notifications.length > 0 ? (
            <ul className="dashboard-notifications-list space-y-3" aria-label="Notifications">
              {dashboardData.notifications.map((notification) => (
                <li
                  key={notification.notification_id}
                  className="dashboard-notification-item p-3 bg-gradient-to-r from-teal-50 to-cyan-50 rounded-lg border-l-4 border-teal-500 text-gray-900 hover:shadow-md transition-shadow duration-200"
                >
                  <div className="flex items-start gap-2">
                    <span className="text-teal-500 mt-1">•</span>
                    <div className="flex-1">
                      <span className="text-gray-900 font-medium">{notification.message}</span>
                      {notification.notification_type && (
                        <span className="ml-2 text-xs px-2 py-1 bg-teal-100 text-teal-700 rounded font-bold">
                          {notification.notification_type}
                        </span>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-center text-gray-700 py-8">
              <Bell className="mx-auto mb-2 text-teal-400" size={40} />
              <p className="font-bold text-gray-900">No notifications yet</p>
              <p className="text-sm mt-1 text-gray-600">You'll see updates here when you get notifications</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Links */}
      <Card className="dashboard-quick-links-card" id="quick-links-card">
        <CardHeader>
          <CardTitle>
            <ExternalLink className="text-teal-500" size={24} />
            <span className="text-gray-900">Quick Links</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="dashboard-quick-links-list space-y-3" aria-label="Quick links">
            <li className="dashboard-quick-link-item">
              <button
                onClick={() => setCurrentComponent('dashboard')}
                className="dashboard-quick-link w-full text-left px-4 py-3 bg-gradient-to-r from-teal-50 to-cyan-50 hover:from-teal-100 hover:to-cyan-100 text-gray-900 hover:text-teal-700 rounded-lg transition-all duration-200 flex items-center justify-between group border-2 border-teal-100 hover:border-teal-300 font-medium"
              >
                <span>Update Portfolio</span>
                <ChevronRight className="text-gray-500 group-hover:text-teal-500 group-hover:translate-x-1 transition-all" size={18} />
              </button>
            </li>
            <li className="dashboard-quick-link-item">
              <button
                onClick={() => setCurrentComponent('applyprojects')}
                className="dashboard-quick-link w-full text-left px-4 py-3 bg-gradient-to-r from-teal-50 to-cyan-50 hover:from-teal-100 hover:to-cyan-100 text-gray-900 hover:text-teal-700 rounded-lg transition-all duration-200 flex items-center justify-between group border-2 border-teal-100 hover:border-teal-300 font-medium"
              >
                <span>Browse Open Roles</span>
                <ChevronRight className="text-gray-500 group-hover:text-teal-500 group-hover:translate-x-1 transition-all" size={18} />
              </button>
            </li>
          </ul>
        </CardContent>
      </Card>

      {/* Skills Showcase - Real Data with API Calls */}
      <Card className="dashboard-skills-showcase-card" id="skills-showcase-card">
        <CardHeader>
          <CardTitle>
            <Award className="text-teal-500" size={24} />
            <span className="text-gray-900">Skills Showcase</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <SkillsShowcase
            skills={skills}
            setSkills={setSkills}
            onSkillsChange={onSkillsChange}
          />
          <Showreel showReel={showReel} toggleShowReel={toggleShowReel} />
        </CardContent>
      </Card>

      {/* Upcoming Auditions - Real Data from Backend */}
      <Card className="dashboard-upcoming-auditions-card" id="upcoming-auditions-card">
        <CardHeader>
          <CardTitle>
            <Calendar className="text-teal-500" size={24} />
            <span className="text-gray-900">Upcoming Auditions</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {dashboardData.upcoming_auditions && dashboardData.upcoming_auditions.length > 0 ? (
            <ul className="dashboard-auditions-list space-y-3" aria-label="Upcoming auditions">
              {dashboardData.upcoming_auditions.map((audition, index) => (
                <li
                  key={index}
                  className="dashboard-audition-item p-3 bg-gradient-to-r from-cyan-50 to-teal-50 rounded-lg border-l-4 border-cyan-500 hover:shadow-md transition-shadow duration-200"
                >
                  <div className="flex items-start gap-3">
                    <Calendar className="text-cyan-500 flex-shrink-0 mt-1" size={18} />
                    <div className="flex-1">
                      <div className="font-bold text-gray-900">{audition.role}</div>
                      <div className="text-sm text-gray-700">"{audition.project}"</div>
                      <div className="text-xs text-gray-600 mt-1 font-medium">{audition.date}</div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-center text-gray-700 py-8">
              <Calendar className="mx-auto mb-2 text-teal-400" size={40} />
              <p className="font-bold text-gray-900">No upcoming auditions</p>
              <p className="text-sm mt-1 text-gray-600">Check back later for new opportunities!</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dashboard Stats - Real Data from Backend */}
      <Card className="col-span-1 md:col-span-3 dashboard-stats-card">
        <CardHeader>
          <CardTitle>
            <Sparkles className="text-teal-500" size={24} />
            <span className="text-gray-900">Your Stats</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="dashboard-stat-item p-4 bg-gradient-to-r from-teal-50 to-teal-100 rounded-lg border-l-4 border-teal-500 shadow-md">
              <div className="text-3xl font-bold text-teal-700">{dashboardData.total_projects || 0}</div>
              <div className="text-sm text-gray-700 mt-1 font-medium">Total Projects</div>
            </div>
            <div className="dashboard-stat-item p-4 bg-gradient-to-r from-emerald-50 to-emerald-100 rounded-lg border-l-4 border-emerald-500 shadow-md">
              <div className="text-3xl font-bold text-emerald-700">{dashboardData.active_projects || 0}</div>
              <div className="text-sm text-gray-700 mt-1 font-medium">Active Projects</div>
            </div>
            <div className="dashboard-stat-item p-4 bg-gradient-to-r from-cyan-50 to-cyan-100 rounded-lg border-l-4 border-cyan-500 shadow-md">
              <div className="text-3xl font-bold text-cyan-700">{dashboardData.skills_count || 0}</div>
              <div className="text-sm text-gray-700 mt-1 font-medium">Skills Added</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>

    <Alert>
      <AlertTitle>Pro Tip</AlertTitle>
      <AlertDescription>
        Keep your profile up to date to increase your chances of being discovered by casting directors and producers.
      </AlertDescription>
    </Alert>
  </>
);

// Sidebar component
const Sidebar = ({ sidebarCollapsed, toggleSidebar, setCurrentComponent, productionHouse }) => (
  <div className={`enthusiast-dashboard-sidebar bg-gradient-to-b from-white via-teal-50 to-white transition-all duration-300 relative ${sidebarCollapsed ? 'w-16' : 'w-64'} shadow-2xl border-r-2 border-teal-200`}>
    <div className="p-5 space-y-6">
      <h1 className={`enthusiast-sidebar-title font-bold ${sidebarCollapsed ? 'text-xl' : 'text-2xl'} mb-8`}>
        <button
          onClick={() => setCurrentComponent('dashboard')}
          className="enthusiast-sidebar-link-main flex items-center focus:outline-none focus:ring-2 focus:ring-teal-400 rounded-lg p-2 hover:bg-teal-100 transition-colors w-full"
          aria-label="Film Dashboard"
        >
          <Film className="text-teal-600 flex-shrink-0" aria-hidden="true" size={28} />
          {!sidebarCollapsed && (
            <span className="ml-3 text-gray-900 font-bold">
              Film Enthusiast
            </span>
          )}
        </button>
      </h1>

      {/* Production House Badge in Sidebar */}
      {productionHouse && !sidebarCollapsed && (
        <div className="mb-4 p-3 bg-gradient-to-r from-teal-100 to-cyan-100 rounded-lg border-2 border-teal-300">
          <div className="flex items-center gap-2">
            <Building2 className="text-teal-600 flex-shrink-0" size={20} />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold text-gray-900 truncate">{productionHouse.production_house?.name || 'Production House'}</p>
              <span className="text-xs text-emerald-600 font-bold">● Active</span>
            </div>
          </div>
        </div>
      )}

      <nav className="enthusiast-sidebar-nav" aria-label="Main navigation">
        <ul className="enthusiast-sidebar-menu space-y-2 mt-4">
          <li className="enthusiast-sidebar-item">
            <button
              onClick={() => setCurrentComponent('myprojects')}
              className="enthusiast-nav-button flex items-center w-full focus:outline-none focus:ring-2 focus:ring-teal-400 rounded-lg p-3 hover:bg-teal-100 transition-all duration-200 group"
              aria-label="My Projects"
            >
              <Film className="text-gray-700 group-hover:text-teal-600 transition-colors flex-shrink-0" aria-hidden="true" />
              {!sidebarCollapsed && (
                <span className="ml-3 text-gray-900 group-hover:text-teal-600 transition-colors font-medium">My Projects</span>
              )}
            </button>
          </li>

          {/* Production House Menu Item - Only show if connected */}
          {productionHouse && (
            <li className="enthusiast-sidebar-item">
              <button
                onClick={() => setCurrentComponent('production')}
                className="enthusiast-nav-button flex items-center w-full focus:outline-none focus:ring-2 focus:ring-teal-400 rounded-lg p-3 bg-gradient-to-r from-teal-100 to-cyan-100 hover:from-teal-200 hover:to-cyan-200 transition-all duration-200 group border-2 border-teal-300"
                aria-label="Production House"
              >
                <Building2 className="text-teal-700 group-hover:text-teal-600 transition-colors flex-shrink-0" aria-hidden="true" />
                {!sidebarCollapsed && (
                  <span className="ml-3 text-gray-900 group-hover:text-teal-600 transition-colors font-bold">Production</span>
                )}
              </button>
            </li>
          )}

          <li className="enthusiast-sidebar-item">
            <Link
              to="/services"
              className="enthusiast-nav-link flex items-center focus:outline-none focus:ring-2 focus:ring-teal-400 rounded-lg p-3 hover:bg-teal-100 transition-all duration-200 group"
              aria-label="Tools"
            >
              <BsTools className="text-gray-700 group-hover:text-teal-600 transition-colors flex-shrink-0" aria-hidden="true" />
              {!sidebarCollapsed && (
                <span className="ml-3 text-gray-900 group-hover:text-teal-600 transition-colors font-medium">Tools</span>
              )}
            </Link>
          </li>
          <li className="enthusiast-sidebar-item">
            <button
              onClick={() => setCurrentComponent('enthusiast-schedule')}
              className="enthusiast-nav-button flex items-center w-full focus:outline-none focus:ring-2 focus:ring-teal-400 rounded-lg p-3 hover:bg-teal-100 transition-all duration-200 group"
              aria-label="My Schedules"
            >
              <Calendar className="text-gray-700 group-hover:text-teal-600 transition-colors flex-shrink-0" aria-hidden="true" />
              {!sidebarCollapsed && (
                <span className="ml-3 text-gray-900 group-hover:text-teal-600 transition-colors font-medium">My Schedules</span>
              )}
            </button>
          </li>
          <li className="enthusiast-sidebar-item">
            <button
              onClick={() => setCurrentComponent('applyprojects')}
              className="enthusiast-nav-button flex items-center w-full focus:outline-none focus:ring-2 focus:ring-teal-400 rounded-lg p-3 hover:bg-teal-100 transition-all duration-200 group"
              aria-label="Apply to Projects"
            >
              <Users className="text-gray-700 group-hover:text-teal-600 transition-colors flex-shrink-0" aria-hidden="true" />
              {!sidebarCollapsed && (
                <span className="ml-3 text-gray-900 group-hover:text-teal-600 transition-colors font-medium">Apply to Projects</span>
              )}
            </button>
          </li>
          <li className="enthusiast-sidebar-item">
            <Link
              to="/blog"
              className="enthusiast-nav-link flex items-center focus:outline-none focus:ring-2 focus:ring-teal-400 rounded-lg p-3 hover:bg-teal-100 transition-all duration-200 group"
              aria-label="Skills Showcase"
            >
              <Award className="text-gray-700 group-hover:text-teal-600 transition-colors flex-shrink-0" aria-hidden="true" />
              {!sidebarCollapsed && (
                <span className="ml-3 text-gray-900 group-hover:text-teal-600 transition-colors font-medium">Skills Showcase</span>
              )}
            </Link>
          </li>
          <li className="enthusiast-sidebar-item">
            <button
              onClick={() => setCurrentComponent('dashboard')}
              className="enthusiast-nav-button flex items-center w-full focus:outline-none focus:ring-2 focus:ring-teal-400 rounded-lg p-3 hover:bg-teal-100 transition-all duration-200 group"
              aria-label="Upcoming Auditions"
            >
              <Calendar className="text-gray-700 group-hover:text-teal-600 transition-colors flex-shrink-0" aria-hidden="true" />
              {!sidebarCollapsed && (
                <span className="ml-3 text-gray-900 group-hover:text-teal-600 transition-colors font-medium">Upcoming Auditions</span>
              )}
            </button>
          </li>
          <li className="enthusiast-sidebar-item">
            <button
              onClick={() => setCurrentComponent('dashboard')}
              className="enthusiast-nav-button flex items-center w-full focus:outline-none focus:ring-2 focus:ring-teal-400 rounded-lg p-3 hover:bg-teal-100 transition-all duration-200 group"
              aria-label="Industry Insights"
            >
              <Briefcase className="text-gray-700 group-hover:text-teal-600 transition-colors flex-shrink-0" aria-hidden="true" />
              {!sidebarCollapsed && (
                <span className="ml-3 text-gray-900 group-hover:text-teal-600 transition-colors font-medium">Industry Insights</span>
              )}
            </button>
          </li>
          <li className="enthusiast-sidebar-item">
            <Link
              to="/toolmanager"
              className="enthusiast-nav-link flex items-center focus:outline-none focus:ring-2 focus:ring-teal-400 rounded-lg p-3 hover:bg-teal-100 transition-all duration-200 group"
            >
              <PiHammerLight className="text-xl text-gray-700 group-hover:text-teal-600 transition-colors flex-shrink-0" />
              {!sidebarCollapsed && (
                <span className="ml-3 text-gray-900 group-hover:text-teal-600 transition-colors font-medium">Tool Manager</span>
              )}
            </Link>
          </li>
        </ul>
      </nav>
    </div>

    <button
      onClick={toggleSidebar}
      style={{ backgroundColor: '#0d9488', color: '#ffffff' }}
      className="enthusiast-sidebar-toggle absolute top-5 -right-3 rounded-full p-2 hover:opacity-90 transition-all shadow-lg hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-teal-400 z-10"
      aria-label={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
    >
      {sidebarCollapsed ? (
        <ChevronRight className="text-white" aria-hidden="true" />
      ) : (
        <ChevronLeft className="text-white" aria-hidden="true" />
      )}
    </button>
  </div>
);

// Main component - WITH ALL BACKEND INTEGRATION
const FilmEnthusiastDashboard = () => {
  const [showReel, setShowReel] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [skills, setSkills] = useState([]);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [currentComponent, setCurrentComponent] = useState('dashboard');

  // Production House State
  const [productionHouse, setProductionHouse] = useState(null);
  const [loadingConnection, setLoadingConnection] = useState(true);

  // Dashboard Data State
  const [dashboardData, setDashboardData] = useState({
    project_activity: [],
    notifications: [],
    upcoming_auditions: [],
    total_projects: 0,
    active_projects: 0,
    skills_count: 0
  });
  const [loadingDashboard, setLoadingDashboard] = useState(true);

  // Fetch all data on mount
  useEffect(() => {
    fetchProductionConnection();
    fetchDashboardData();
    fetchSkills();
  }, []);

  // Fetch production house connection status
  const fetchProductionConnection = async () => {
    setLoadingConnection(true);
    try {
      const connection = await productionApi.getConnection();
      if (connection && connection.status === 'active') {
        setProductionHouse(connection);
      } else {
        setProductionHouse(null);
      }
    } catch (err) {
      setProductionHouse(null);
    } finally {
      setLoadingConnection(false);
    }
  };

  // Fetch dashboard overview
  const fetchDashboardData = async () => {
    setLoadingDashboard(true);
    try {
      const data = await getDashboardOverview();
      setDashboardData(data);
      setNotifications(data.notifications || []);
    } catch (err) {
    } finally {
      setLoadingDashboard(false);
    }
  };

  // Fetch skills
  const fetchSkills = async () => {
    try {
      const skillsData = await getSkills();
      const skillNames = skillsData.map(skill => skill.skill_name);
      setSkills(skillNames);
    } catch (err) {
      setSkills([]);
    }
  };

  const toggleShowReel = () => {
    setShowReel(!showReel);
  };

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleProductionLeave = () => {
    setProductionHouse(null);
    setCurrentComponent('dashboard');
  };

  // Render the appropriate component based on selection
  const renderComponent = () => {
    // Show loading while fetching connection status
    if (loadingConnection || loadingDashboard) {
      return <LoadingFallback />;
    }

    switch (currentComponent) {
      case 'production':
        return productionHouse ? (
          <ProductionMenu
            productionHouse={productionHouse}
            onLeaveProduction={handleProductionLeave}
          />
        ) : (
          <ProductionHouseLinkingCard onConnectionUpdate={fetchProductionConnection} />
        );

      case 'myprojects':
        return (
          <Suspense fallback={<LoadingFallback />}>
            <LazyMyProjects productionHouse={productionHouse} />  {/* ✅ prop here */}
          </Suspense>
        );
      case 'enthusiast-schedule':
        return (
          <Suspense fallback={<LoadingFallback />}>
            <LazyEnthusiastSchedule />
          </Suspense>
        );
      case 'applyprojects':
        return (
          <Suspense fallback={<LoadingFallback />}>
            <LazyApplyProjects />
          </Suspense>
        );
      default:
        return (
          <>
            {!productionHouse && (
              <div className="mb-6">
                <ProductionHouseLinkingCard onConnectionUpdate={fetchProductionConnection} />
              </div>
            )}

            <DashboardContent
              skills={skills}
              setSkills={setSkills}
              dashboardData={dashboardData}
              showReel={showReel}
              toggleShowReel={toggleShowReel}
              setCurrentComponent={setCurrentComponent}
              onSkillsChange={fetchDashboardData}
            />
          </>
        );
    }
  };

  return (
    <div className="film-enthusiast-dashboard-root w-full m-0 p-0 overflow-x-hidden">
      <SEO
        title="Film Enthusiast Dashboard"
        description="Explore film projects, connect with filmmakers, and track your favourite productions on ProducerBuddy."
        url="https://producerbuddy.pilomation.com/filmenthusiast"
        robots="noindex, nofollow"
      />
      <SubNav />
      <div className="film-enthusiast-dashboard-wrapper flex w-full min-h-screen bg-gradient-to-br from-teal-50 via-white to-teal-100 m-0 p-0">
        <Sidebar
          sidebarCollapsed={sidebarCollapsed}
          toggleSidebar={toggleSidebar}
          setCurrentComponent={setCurrentComponent}
          productionHouse={productionHouse}
        />

        <main className="film-enthusiast-main-content flex-1 p-8 max-w-full" role="main" aria-label="Dashboard content">
          {renderComponent()}
        </main>
      </div>
    </div>
  );
};

export default FilmEnthusiastDashboard;
